import { createGuid, Cartesian3, Cartesian2 } from "cesium";
import { VerticalAlignment } from "./vertical-alignment/vertical-alignment";
import { AlignmentPointType, } from "./interface";
import { perpendicularDistanceFromPointToLine } from "../../utilities/math-utils";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { linkPvisToIps, relocatePvis } from "./vertical-alignment/pvi-ip-link";
import { CurveDataType, HorizontalAlignmentType, } from "./horizontal-alignment/interface";
import { AssetType, } from "../../types";
import { TangentHorizontalAlignment } from "./horizontal-alignment/tangent-horizontal-alignment";
import { SimpleCircularHorizontalAlignment } from "./horizontal-alignment/simple-circular-horizontal-alignment";
import { SpiralTransitionedHorizontalAlignment } from "./horizontal-alignment/spiral-transitioned-horizontal-alignment";
import { QuadraticBezierHorizontalAlignment } from "./horizontal-alignment/quadratic-bezier-horizontal-alignment";
import { DEFAULT_STATION_INTERVAL, formatNoLabel, formatNoWithAdditionalDistanceLabel, } from "../../site/arbitrary-line/constants";
import { PointType, } from "../../site/arbitrary-line/types";
import { DEFAULT_TERRIAN_PROFILE_VIEW_STEP_SIZE } from "../../site/road/defaults";
import { LineWorkUtils } from "../../utilities/line-work-utils";
// there are two types of alignment
// horzontal alignment (points and connection by curve)
// vertical alignment (point and height, distance and height, percentage and height)
// AI 生成経路の縦断再構築で、曲線投影距離が非単調になった際に与える最小進み幅（m）。
// 同一測点かどうかの突合には utilities/line-profile-geometry-utils の DISTANCE_TOLERANCE（1mm）を
// 使うため、意図的にずらした点が同一と判定されないよう、その 10 倍を確保する。
// 不変条件: DISTANCE_TOLERANCE < VERTICAL_PROFILE_MIN_STEP_M
const VERTICAL_PROFILE_MIN_STEP_M = 1e-2;
/** Below this 2D length a centerline segment carries no usable direction. */
const DEGENERATE_SEGMENT_LENGTH = 1e-6;
export class Alignment {
    // ------------------------ Public Instance Properties ------------------------
    id;
    name;
    // ------------------------ Private Instance Properties ------------------------
    _surveyPointsMap;
    _horizontalAlignment;
    _verticalAlignment;
    _length;
    _stationInterval;
    _curvePoints;
    _ipLinePoints;
    _centerLinePoints;
    // Maps a removed centerline point's id to the id it merged into (zero-distance dedup).
    _centerLinePointMergeAliases;
    // Reverse of the above: current point id -> the raw IP id it absorbed, if any.
    _survivorRawIpIds;
    constructor(id) {
        this.id = id || createGuid();
        this._surveyPointsMap = new Map();
        this._horizontalAlignment = new SimpleCircularHorizontalAlignment();
        this._verticalAlignment = new VerticalAlignment(this._horizontalAlignment);
        this._curvePoints = [];
        this._stationInterval = DEFAULT_STATION_INTERVAL; // default
        this._length = 0;
        this._ipLinePoints = [];
        this._centerLinePoints = [];
        this._centerLinePointMergeAliases = new Map();
        this._survivorRawIpIds = new Map();
    }
    // --------------------------Public Getters/Setters ----------------------------
    get assetType() {
        return AssetType.ALIGNMENT;
    }
    get horizontalAlignment() {
        return this._horizontalAlignment;
    }
    get verticalAlignment() {
        return this._verticalAlignment;
    }
    get surveyPoints() {
        return Array.from(this._surveyPointsMap.values());
    }
    get ipLinePoints() {
        return this._ipLinePoints;
    }
    get centerLinePoints() {
        return this._centerLinePoints;
    }
    get curvePoints() {
        return this._curvePoints;
    }
    /** Follows zero-distance merge aliases until reaching an id with no further alias. */
    resolveMergedPointId(id) {
        const seen = new Set();
        let current = id;
        while (this._centerLinePointMergeAliases.has(current) &&
            !seen.has(current)) {
            seen.add(current);
            current = this._centerLinePointMergeAliases.get(current);
        }
        return current;
    }
    /** Finds a centerline point by id, falling back to its IP projection point (following merge aliases); undefined if neither exists. */
    resolveCenterLinePoint(id) {
        const resolvedId = this.resolveMergedPointId(id);
        const direct = this._centerLinePoints.find((p) => p.id === resolvedId);
        if (direct)
            return direct;
        const resolvedIpId = this.resolveMergedPointId(`IP_${id}`);
        return this._centerLinePoints.find((p) => p.id === resolvedIpId);
    }
    /** Resolves a live point id to the raw IP id it absorbed, if any; otherwise returns it unchanged. */
    getStableMarkerIdForPoint(pointId) {
        return this._survivorRawIpIds.get(pointId) ?? pointId;
    }
    /**
     * Nearest neighbor from `index` in the given step direction that is far enough away to give a
     * meaningful direction. Points merged only on exact equality, so near-coincident ones remain.
     */
    findDistinctCenterlineNeighbor(index, step) {
        const origin = this._centerLinePoints[index].localPointWithAdjustedHeight;
        for (let i = index + step; i >= 0 && i < this._centerLinePoints.length; i += step) {
            const candidate = this._centerLinePoints[i];
            if (Cartesian2.distance(candidate.localPointWithAdjustedHeight, origin) >
                DEGENERATE_SEGMENT_LENGTH) {
                return candidate;
            }
        }
        return undefined;
    }
    /**
     * Direction at a dense `centerLinePoints` index, blended from its immediate neighbors. Unlike
     * blending over distant neighbors, this stays close to the true tangent even at BC/EC and
     * straight-to-straight IPs.
     */
    getBlendedDirectionAtCenterlineIndex(index) {
        const current = this._centerLinePoints[index];
        const prev = this.findDistinctCenterlineNeighbor(index, -1);
        const next = this.findDistinctCenterlineNeighbor(index, 1);
        const upDir = Cartesian3.UNIT_Z;
        if (!prev) {
            return EarthBrainUtils.getDirectionOfPointOnSitePath(current.localPointWithAdjustedHeight, next.localPointWithAdjustedHeight, upDir);
        }
        if (!next) {
            return EarthBrainUtils.getDirectionOfPointOnSitePath(prev.localPointWithAdjustedHeight, current.localPointWithAdjustedHeight, upDir);
        }
        const indirection = EarthBrainUtils.getDirectionOfPointOnSitePath(prev.localPointWithAdjustedHeight, current.localPointWithAdjustedHeight, upDir);
        const outdirection = EarthBrainUtils.getDirectionOfPointOnSitePath(current.localPointWithAdjustedHeight, next.localPointWithAdjustedHeight, upDir);
        return EarthBrainUtils.addSiteDirection(indirection, outdirection);
    }
    getDirectionAtCenterlinePoint(pointId) {
        const index = this._centerLinePoints.findIndex((p) => p.id === pointId);
        if (index === -1) {
            return undefined;
        }
        return this.getBlendedDirectionAtCenterlineIndex(index);
    }
    /**
     * Directions for positions taken from the centerline, each paired with the index of the
     * `centerLinePoints` segment it was derived from.
     */
    getDirectionsAlongPositions(positions, segmentIndices) {
        const upDir = Cartesian3.UNIT_Z;
        return positions.map((position, index) => {
            const segmentIndex = segmentIndices[index];
            const start = this._centerLinePoints[segmentIndex].localPointWithAdjustedHeight;
            const end = this._centerLinePoints[segmentIndex + 1].localPointWithAdjustedHeight;
            if (Cartesian2.distance(start, position) < 1e-3) {
                return this.getBlendedDirectionAtCenterlineIndex(segmentIndex);
            }
            if (Cartesian2.distance(end, position) < 1e-3) {
                return this.getBlendedDirectionAtCenterlineIndex(segmentIndex + 1);
            }
            return EarthBrainUtils.getDirectionOfPointOnSitePath(start, end, upDir);
        });
    }
    /**
     * Point on path with its direction. Reuses `getBlendedDirectionAtCenterlineIndex` when the
     * distance lands exactly on a `centerLinePoints` entry (always true for station/BC/EC/BP/EP/IP
     * marker clicks); otherwise falls back to the bounding segment's own direction.
     */
    getSectionOrientationAt3dDistance(distanceFromStart) {
        const { pointOnPath, bounds } = this.getPointInfoAt3dDistance(distanceFromStart);
        if (!pointOnPath) {
            return undefined;
        }
        const index = this._centerLinePoints.findIndex((p) => Cartesian2.distance(p.localPointWithAdjustedHeight, pointOnPath) < 1e-3);
        if (index !== -1) {
            return {
                pointOnPath,
                direction: this.getBlendedDirectionAtCenterlineIndex(index),
            };
        }
        const direction = EarthBrainUtils.getDirectionOfPointOnSitePath(new Cartesian3(bounds[0].x, bounds[0].y, 0), new Cartesian3(bounds[1].x, bounds[1].y, 0), new Cartesian3(0, 0, 1));
        return { pointOnPath, direction };
    }
    /**
     * Total 3D length of all segments from rawPathPoints (in meters).
     * Sum of raw geometry lengths before interpolation.
     */
    get length() {
        return this._length;
    }
    /**
     * Total 2D horizontal length (XY plane only) from rawPathPoints (in meters).
     * Sum of raw geometry lengths ignoring Z coordinate.
     */
    get lengthOnDatum() {
        return this._horizontalAlignment.length;
    }
    get stationInterval() {
        return this._stationInterval;
    }
    get pointsToCreateCrossSection() {
        //BP, EP, IP Projection Points, Station Points, BC, EC
        const pointsForCrossSection = this.centerLinePoints.filter((point) => {
            return (point.pointType.includes(AlignmentPointType.BEGIN_POINT) ||
                point.pointType.includes(AlignmentPointType.END_POINT) ||
                point.pointType.includes(AlignmentPointType.IP_PROJECTION) ||
                point.pointType.includes(AlignmentPointType.STATION_POINT) ||
                point.pointType.includes(AlignmentPointType.BEGIN_CURVE_POINT) ||
                point.pointType.includes(AlignmentPointType.END_CURVE_POINT));
        });
        const crossSectionPoints = pointsForCrossSection.map((stationPoint) => {
            return {
                id: stationPoint.id,
                point: stationPoint.localPointWithAdjustedHeight,
                pointType: PointType.STATION_POINT,
                label: stationPoint.label,
            };
        });
        return crossSectionPoints;
    }
    // --------------------------Public Methods ----------------------------
    dispose() {
        this._horizontalAlignment.dispose();
        this._verticalAlignment.dispose();
        this._surveyPointsMap.clear();
        this._ipLinePoints.length = 0;
        this._centerLinePoints.length = 0;
        this._curvePoints.length = 0;
        this._length = 0;
    }
    //---------------------------Initialization---------------------------
    initializeFromAlignmentData(surveyPoints, ipPoints, horizontalAlignmentData, verticalAlignmentData, stationInterval = DEFAULT_STATION_INTERVAL) {
        if (ipPoints.length < 2 ||
            surveyPoints.length < 2 ||
            horizontalAlignmentData.data.length < 1 ||
            verticalAlignmentData.length < 2) {
            throw new Error("Invalid input data");
        }
        //pre-process
        // Survey Points may not be ordered
        this._surveyPointsMap.clear();
        surveyPoints.forEach((sp) => {
            this._surveyPointsMap.set(sp.id, sp);
        });
        // IP Points are ordered in order they are received
        //IP 点についても、持つことは可能（省略可）である。なお、ソフトウェアが読み込む際には、点列は並び順とする。
        let cumulativeDistance = 0;
        for (let i = 0; i < ipPoints.length; i++) {
            let distanceWithPreviousPoint = 0;
            if (i > 0) {
                const previousPoint = this._surveyPointsMap.get(ipPoints[i - 1]);
                const currentPoint = this._surveyPointsMap.get(ipPoints[i]);
                distanceWithPreviousPoint = Cartesian2.distance(previousPoint.localPoint, currentPoint.localPoint);
            }
            cumulativeDistance += distanceWithPreviousPoint;
            const surveyPointData = this._surveyPointsMap.get(ipPoints[i]);
            const ipAlignmentPoint = {
                id: surveyPointData.id,
                localPoint: surveyPointData.localPoint,
                localPointWithAdjustedHeight: surveyPointData.localPoint,
                distanceFromPreviousPoint: distanceWithPreviousPoint,
                distanceFromStart: cumulativeDistance,
                pointType: [
                    i == 0
                        ? AlignmentPointType.BEGIN_POINT
                        : i == ipPoints.length - 1
                            ? AlignmentPointType.END_POINT
                            : AlignmentPointType.INTERSECTION_POINT,
                ],
                label: surveyPointData.name || "IP-" + i,
            };
            this._ipLinePoints.push(ipAlignmentPoint);
            surveyPointData.name = ipAlignmentPoint.label;
        }
        //computation
        switch (horizontalAlignmentData.type) {
            case HorizontalAlignmentType.TANGENT:
                this._horizontalAlignment = new TangentHorizontalAlignment();
                break;
            case HorizontalAlignmentType.SIMPLE_CIRCULAR:
                this._horizontalAlignment = new SimpleCircularHorizontalAlignment();
                break;
            case HorizontalAlignmentType.SPIRAL_TRANSITIONED:
                this._horizontalAlignment = new SpiralTransitionedHorizontalAlignment();
                break;
            case HorizontalAlignmentType.QUADRATIC_BEZIER:
                this._horizontalAlignment = new QuadraticBezierHorizontalAlignment();
                break;
            default:
                throw new Error("Invalid Horizontal Alignment Type");
        }
        this._horizontalAlignment.initializeFromIPPoints(this._ipLinePoints);
        this._verticalAlignment.updateData(verticalAlignmentData);
        this._stationInterval = stationInterval;
        this.postCompute();
        return this._ipLinePoints.map((ip) => ip.id);
    }
    /**
     * 全 IP 点の z から縦断プロファイルを再構築する（AI 生成経路用）。
     * 末尾追加ベースの通常フローでは縦断が始終点 2 点に潰れるため、
     * AI が算出した各測点の高さを PVI として保持する目的で使用する。
     * 水平距離は突合先（Road 側）と同じ getClosestPointInfoOnPath に揃える。
     */
    setVerticalProfileFromIPHeights() {
        if (this._ipLinePoints.length < 2) {
            return;
        }
        const data = [];
        let previousDistance = Number.NEGATIVE_INFINITY;
        for (const ip of this._ipLinePoints) {
            // Road 側の縦断データ突合と同一関数で距離を求め、
            // 同じ測点の水平距離が計算経路の違いでずれないようにする。
            let distance = this.getClosestPointInfoOnPath(ip.localPoint).distanceFromStart_2D;
            // 曲線投影が非単調になる場合の保険として距離の単調性を保証する。
            if (distance <= previousDistance) {
                distance = previousDistance + VERTICAL_PROFILE_MIN_STEP_M;
            }
            previousDistance = distance;
            data.push({ horizontalDistance: distance, elevation: ip.localPoint.z });
        }
        this._verticalAlignment.updateData(data);
        this.postCompute();
    }
    /**
     * 曲線生成で作られた測点（BC / EC）の高さを縦断プロファイルから補完する。
     * 水平線形は 2D 計算のため生成点の z が 0 のままになるのを補正する目的で使う。
     * IP 点は入力高さを持つため対象外。AI 生成経路の構築直後に呼ぶ想定。
     */
    applyVerticalProfileToGeneratedSurveyPoints() {
        const ipPointIds = new Set(this._ipLinePoints.map((ip) => ip.id));
        for (const surveyPoint of this._surveyPointsMap.values()) {
            if (ipPointIds.has(surveyPoint.id)) {
                continue;
            }
            // localPoint は水平線形のセグメントと共有する実体のため、
            // z を直接書き換えることで postCompute を跨いでも補正が保持される。
            const { distanceFromStart } = this._horizontalAlignment.getClosestPointInfo(surveyPoint.localPoint);
            surveyPoint.localPoint.z =
                this._verticalAlignment.getElevationForHorizontalDistance(distanceFromStart);
        }
    }
    /**
     * This function will add control point at index will also update controlPointBufferMap and updateRoadGeometry()
     * @param index INDEX at which this function point will get added
     * @param localPos POSITION of control point in local space
     * @param restoreIpId undo-only: reuse the deleted IP point's ID (middle insertion only)
     */
    addIPPointAtIndex(index, localPoint, restoreIpId) {
        const existingPoint = this._ipLinePoints.find((p) => p.localPoint.equals(localPoint));
        if (existingPoint) {
            console.warn("The control point picked already exists.");
            return;
        }
        // Length after insertion; decides the path before mutating IP points
        const lengthAfterInsert = this._ipLinePoints.length + 1;
        const isMiddleInsert = lengthAfterInsert >= 2 && index !== 0 && index !== lengthAfterInsert - 1;
        const activeRestoreIpId = isMiddleInsert ? restoreIpId : undefined;
        const currentPoint = localPoint.clone();
        const ipPoint = {
            id: activeRestoreIpId ?? createGuid(),
            localPoint: currentPoint,
            localPointWithAdjustedHeight: currentPoint.clone(),
            distanceFromPreviousPoint: 0,
            distanceFromStart: 0,
            pointType: [AlignmentPointType.BEGIN_POINT],
            label: "",
        };
        const insertIpPoint = () => {
            this._ipLinePoints.splice(index, 0, ipPoint);
            this.fixLabelsOfIpPoints();
            const surveyPoint = {
                localPoint: ipPoint.localPoint,
                id: ipPoint.id,
                name: ipPoint.label,
            };
            this._surveyPointsMap.set(surveyPoint.id, surveyPoint);
        };
        if (lengthAfterInsert < 2) {
            insertIpPoint();
        }
        else {
            const updateFunc = () => {
                let cumulativeDistance = 0;
                for (let i = 0; i < this._ipLinePoints.length; i++) {
                    let distanceWithPreviousPoint = 0;
                    if (i > 0) {
                        distanceWithPreviousPoint = Cartesian2.distance(this._ipLinePoints[i - 1].localPoint, this._ipLinePoints[i].localPoint);
                    }
                    cumulativeDistance += distanceWithPreviousPoint;
                    this._ipLinePoints[i].distanceFromPreviousPoint =
                        distanceWithPreviousPoint;
                    this._ipLinePoints[i].distanceFromStart = cumulativeDistance;
                    if (i === 0) {
                        this._ipLinePoints[i].pointType = [AlignmentPointType.BEGIN_POINT];
                        this._ipLinePoints[i].label = "BP";
                    }
                    else if (i === this._ipLinePoints.length - 1) {
                        this._ipLinePoints[i].pointType = [AlignmentPointType.END_POINT];
                        this._ipLinePoints[i].label = "EP";
                    }
                    else {
                        this._ipLinePoints[i].pointType = [
                            AlignmentPointType.INTERSECTION_POINT,
                        ];
                        this._ipLinePoints[i].label = "IP-" + i;
                    }
                }
                this._horizontalAlignment.initializeFromIPPoints(this._ipLinePoints);
                let newVerticalAlignmentData = this._verticalAlignment.verticalAlignmentData || [];
                if (index === this._ipLinePoints.length - 1 ||
                    newVerticalAlignmentData.length < 2) {
                    newVerticalAlignmentData = [
                        {
                            horizontalDistance: 0,
                            elevation: this._ipLinePoints[0].localPoint.z,
                        },
                        {
                            horizontalDistance: this._horizontalAlignment.length,
                            elevation: this._ipLinePoints[this._ipLinePoints.length - 1].localPoint.z,
                        },
                    ];
                }
                this._verticalAlignment.updateData(newVerticalAlignmentData);
                this.postCompute();
            };
            if (!isMiddleInsert) {
                insertIpPoint();
                updateFunc();
            }
            else {
                this.recomputeAlignmentWhileEdit(() => {
                    insertIpPoint();
                    updateFunc();
                });
            }
        }
        return ipPoint.id;
    }
    updateIPPointPosition(pointInfo, clickedControlPointID) {
        const ipPointData = this._ipLinePoints.find((p) => p.id === clickedControlPointID);
        this.recomputeAlignmentWhileEdit(() => {
            //update control point buffer
            ipPointData.localPoint = pointInfo.localPosition;
        });
    }
    /**
     * Remove an IP point and recompute the alignment.
     * Removing the last IP point resets the vertical alignment to 2 points (same as appending at the end).
     */
    removeIPPoint(clickedControlPointID) {
        const index = this._ipLinePoints.findIndex((p) => p.id === clickedControlPointID);
        if (index === -1) {
            return;
        }
        // Fewer than 2 IP points after removal: no alignment can be computed
        if (this._ipLinePoints.length - 1 < 2) {
            this._ipLinePoints.splice(index, 1);
            return;
        }
        // Last IP point: reset vertical alignment to [start z, new end z]
        if (index === this._ipLinePoints.length - 1) {
            this._ipLinePoints.splice(index, 1);
            this._horizontalAlignment.initializeFromIPPoints(this._ipLinePoints);
            this._verticalAlignment.updateData([
                {
                    horizontalDistance: 0,
                    elevation: this._ipLinePoints[0].localPoint.z,
                },
                {
                    horizontalDistance: this._horizontalAlignment.length,
                    elevation: this._ipLinePoints[this._ipLinePoints.length - 1].localPoint.z,
                },
            ]);
            this.postCompute();
            return;
        }
        this.recomputeAlignmentWhileEdit(() => {
            this._ipLinePoints.splice(index, 1);
            this._horizontalAlignment.initializeFromIPPoints(this._ipLinePoints);
        });
    }
    /**
     * Return each IP point's horizontal distance from the start on the current horizontal alignment, in ipLinePoints order.
     * Uses getClosestPointInfo so it is valid mid-recompute (before postCompute).
     */
    computeIpHorizontalDistances() {
        return this._ipLinePoints.map((ip) => ({
            ipId: ip.id,
            horizontalDistance: this._horizontalAlignment.getClosestPointInfo(ip.localPoint).distanceFromStart,
        }));
    }
    //road can be either made completely straight or set to default (combination of straight, clothoid/quadratic, arc)
    setTypeToHorizontalAlignment(type) {
        if (!this._horizontalAlignment) {
            throw new Error("Horizontal Alignment is not initialized");
        }
        // This will recompute horizontal alignment, previous Curve Data will be discarded
        if (this.horizontalAlignment.type === type) {
            return;
        }
        this.recomputeAlignmentWhileEdit(() => {
            let newHorizontalAlignment;
            if (type === HorizontalAlignmentType.TANGENT) {
                newHorizontalAlignment = new TangentHorizontalAlignment();
            }
            else if (type === HorizontalAlignmentType.SIMPLE_CIRCULAR) {
                newHorizontalAlignment = new SimpleCircularHorizontalAlignment();
            }
            else if (type === HorizontalAlignmentType.SPIRAL_TRANSITIONED) {
                newHorizontalAlignment = new SpiralTransitionedHorizontalAlignment();
            }
            else {
                throw new Error("Invalid Horizontal Alignment Type");
            }
            newHorizontalAlignment.initializeFromIPPoints(this._ipLinePoints);
            this._horizontalAlignment = newHorizontalAlignment;
        });
    }
    /**
     * It assumes Horizontal Alignment is already updated and ready
     * @param data
     */
    updateVerticalAlignmentData(data) {
        this._verticalAlignment.updateData(data);
        this.postCompute();
    }
    updateStationInterval(stationInterval) {
        if (this._stationInterval === stationInterval)
            return;
        this._stationInterval = stationInterval;
        this.postCompute();
    }
    //---------------------------Public Methods---------------------------
    toJSON() {
        const horizontalAlignment = [];
        const curveSegments = this._horizontalAlignment.segments;
        for (const curveSegment of curveSegments) {
            switch (curveSegment.type) {
                case CurveDataType.LINEAR:
                    horizontalAlignment.push({
                        type: curveSegment.type,
                        startPointId: curveSegment.startPoint.id,
                        endPointId: curveSegment.endPoint.id,
                    });
                    break;
                case CurveDataType.THREE_POINT_ARC:
                    const arcSegment = curveSegment;
                    horizontalAlignment.push({
                        type: CurveDataType.THREE_POINT_ARC,
                        startPointId: arcSegment.startPoint.id,
                        throughPointId: arcSegment.throughPoint.id,
                        endPointId: arcSegment.endPoint.id,
                    });
                    break;
                case CurveDataType.QUADRATIC_BEZIER:
                    {
                        const quadraticBezierSegment = curveSegment;
                        horizontalAlignment.push({
                            type: CurveDataType.QUADRATIC_BEZIER,
                            pointIds: quadraticBezierSegment.points.map((p) => p.id),
                            startPointId: quadraticBezierSegment.startPoint.id,
                            endPointId: quadraticBezierSegment.endPoint.id,
                        });
                    }
                    break;
                case CurveDataType.CLOTHOID:
                    {
                        const clothoidSegment = curveSegment;
                        horizontalAlignment.push({
                            type: CurveDataType.CLOTHOID,
                            startPointId: clothoidSegment.startPoint.id,
                            endPointId: clothoidSegment.endPoint.id,
                            clothoidType: clothoidSegment.data.clothoidType,
                            radius: clothoidSegment.data.radius,
                            parameterA: clothoidSegment.data.parameterA,
                            anchorPointId: clothoidSegment.data.anchorPointId,
                            length: clothoidSegment.data.length,
                        });
                    }
                    break;
                case CurveDataType.CIRCULAR_SEGMENT:
                    {
                        const circularSegment = curveSegment;
                        horizontalAlignment.push({
                            type: CurveDataType.CIRCULAR_SEGMENT,
                            startPointId: circularSegment.startPoint.id,
                            endPointId: circularSegment.endPoint.id,
                            anchorPointId: circularSegment.anchorPoint.id,
                        });
                    }
                    break;
            }
        }
        const verticalAlignment = [];
        this._verticalAlignment.verticalAlignmentData.forEach((v) => {
            verticalAlignment.push({
                elevation: v.elevation,
                horizontalDistance: v.horizontalDistance,
            });
        });
        return {
            horizontalAlignment,
            verticalAlignment,
            surveyPoints: Array.from(this._surveyPointsMap.values()).map((p) => ({
                localPoint: p.localPoint,
                id: p.id,
                name: p.name,
            })),
            ipPointIds: this._ipLinePoints.map((p) => p.id),
        };
    }
    getIndexOfPointOnIPPath(point) {
        const perpDistances = [];
        for (let i = 1; i < this._ipLinePoints.length; i++) {
            const perpDistance = perpendicularDistanceFromPointToLine(point, this._ipLinePoints[i - 1].localPoint, this._ipLinePoints[i].localPoint, true);
            perpDistances.push(perpDistance);
        }
        const indexOfminDistance = perpDistances.indexOf(Math.min(...perpDistances));
        return indexOfminDistance;
    }
    getInfoOfPointOfVerticalIntersections() {
        const verticalAlignmentData = this._verticalAlignment.verticalAlignmentData;
        const horizontalDistances = verticalAlignmentData.map((data) => data.horizontalDistance);
        const sortedHorizontalDistances = [...horizontalDistances].sort((a, b) => a - b);
        const horizontalPoints = this._horizontalAlignment.getInfoAtDistanceInBatch(sortedHorizontalDistances);
        const elevations = this._verticalAlignment.getElevationForHorizontalDistanceInBatch(sortedHorizontalDistances);
        const points = [];
        for (let i = 0; i < horizontalPoints.length; i++) {
            points.push({
                horizontalDistance: sortedHorizontalDistances[i],
                elevation: elevations[i],
                point: new Cartesian3(horizontalPoints[i].x, horizontalPoints[i].y, elevations[i]),
            });
        }
        return points;
    }
    getPointInfoAt2dDistance(distanceFromStart) {
        //get 2d point from horizontal alignment
        const pointOn2D = this._horizontalAlignment.getPointInfoAtDistance(distanceFromStart);
        //get elevation from vertical alignment
        const elevation = this._verticalAlignment.getElevationForHorizontalDistance(distanceFromStart);
        const pointOn3D = Cartesian3.fromElements(pointOn2D.x, pointOn2D.y, elevation);
        return pointOn3D;
    }
    getClosestPointInfoOnPath(point) {
        // it should not be minimum distance between point and path in 3d space but
        // it should be minimum distance (perpendicular distance) in 2d space
        // elevation should be obtained separately
        const getIndexForProbableSegment = (point, pathpoints) => {
            const perpDistances = [];
            for (let i = 1; i < pathpoints.length; i++) {
                const perpDistance = perpendicularDistanceFromPointToLine(point, pathpoints[i - 1], pathpoints[i], true);
                perpDistances.push(perpDistance);
            }
            const indexOfminDistance = perpDistances.indexOf(Math.min(...perpDistances));
            return indexOfminDistance;
        };
        const ipPointWithoutZ = this._ipLinePoints.map((p) => {
            return new Cartesian3(p.localPoint.x, p.localPoint.y, 0);
        });
        const ipIndex = getIndexForProbableSegment(point, ipPointWithoutZ);
        const info = this._horizontalAlignment.getClosestPointInfo(point);
        const elevation = this._verticalAlignment.getElevationForHorizontalDistance(info.distanceFromStart);
        const distanceFromStart_3D = this.getCorrDistanceFromStartPoint(info.distanceFromStart);
        return {
            point: new Cartesian3(info.point.x, info.point.y, elevation),
            distanceFromStart_2D: info.distanceFromStart,
            distanceFromStart_3D: distanceFromStart_3D,
            ipIndex: ipIndex,
        };
    }
    getCorrDistanceFromStartPoint(projectedDistanceFromStart) {
        let distanceBtwPathPointsEllipsoid = 0;
        let distanceFromStart = 0;
        for (let i = 0; i < this._centerLinePoints.length - 1; i++) {
            const distBtwPathPtEllipsoid = Cartesian2.distance(this._centerLinePoints[i].localPointWithAdjustedHeight, this._centerLinePoints[i + 1].localPointWithAdjustedHeight);
            const distBtwPathPt = Cartesian3.distance(this._centerLinePoints[i].localPointWithAdjustedHeight, this._centerLinePoints[i + 1].localPointWithAdjustedHeight);
            distanceBtwPathPointsEllipsoid += distBtwPathPtEllipsoid;
            if (distanceBtwPathPointsEllipsoid >= projectedDistanceFromStart) {
                const remainingDistBtwPathPoints = projectedDistanceFromStart -
                    distanceBtwPathPointsEllipsoid +
                    distBtwPathPtEllipsoid;
                const proportion = remainingDistBtwPathPoints / distBtwPathPtEllipsoid;
                distanceFromStart += proportion * distBtwPathPt;
                break;
            }
            else {
                distanceFromStart += distBtwPathPt;
            }
        }
        return distanceFromStart;
    }
    getCorrProjectedDistanceFromStartPoint(distanceFromStart) {
        let distanceBtwPathPointsEllipsoid = 0;
        let distanceBtwPathPoints = 0;
        for (let i = 0; i < this._centerLinePoints.length - 1; i++) {
            const distBtwPathPtEllipsoid = Cartesian2.distance(this._centerLinePoints[i].localPointWithAdjustedHeight, this._centerLinePoints[i + 1].localPointWithAdjustedHeight);
            const distBtwPathPt = Cartesian3.distance(this._centerLinePoints[i].localPointWithAdjustedHeight, this._centerLinePoints[i + 1].localPointWithAdjustedHeight);
            distanceBtwPathPoints += distBtwPathPt;
            if (distanceBtwPathPoints >= distanceFromStart) {
                const remainingDistBtwPathPoints = distanceFromStart - distanceBtwPathPoints + distBtwPathPt;
                const proportion = remainingDistBtwPathPoints / distBtwPathPt;
                distanceBtwPathPointsEllipsoid += proportion * distBtwPathPtEllipsoid;
                break;
            }
            else {
                distanceBtwPathPointsEllipsoid += distBtwPathPtEllipsoid;
            }
        }
        return distanceBtwPathPointsEllipsoid;
    }
    getPointInfoAt3dDistance(distanceFromStart) {
        if (distanceFromStart > this.length) {
            return { pointOnPath: undefined, bounds: [undefined, undefined] };
        }
        let computedDistance = 0;
        let bounds = [undefined, undefined];
        let distanceFromFirstBound = 0;
        let prevPoint;
        let pointOnPath;
        for (const pathPoint of this._centerLinePoints) {
            if (prevPoint) {
                const distance = Cartesian3.distance(prevPoint, pathPoint.localPointWithAdjustedHeight);
                if (distance === 0) {
                    continue;
                }
                computedDistance += distance;
                if (computedDistance >= distanceFromStart) {
                    bounds = [prevPoint, pathPoint.localPointWithAdjustedHeight];
                    distanceFromFirstBound =
                        distance - computedDistance + distanceFromStart;
                    const direction = EarthBrainUtils.getDirBetweenTwoCartesians(prevPoint, pathPoint.localPointWithAdjustedHeight);
                    pointOnPath = Cartesian3.add(prevPoint, Cartesian3.multiplyByScalar(direction, distanceFromFirstBound, new Cartesian3()), new Cartesian3());
                    break;
                }
            }
            prevPoint = pathPoint.localPointWithAdjustedHeight;
        }
        return { pointOnPath, bounds };
    }
    get2dViewerData(site) {
        const lineProfileData = {
            pointCloudOutline: [],
            lineWorksOutline: [],
            keyPointIndices: [],
            slopeBreakIndices: [],
        };
        // Use pathInfo.pathPoints if available (for road/trench-based centerlines),
        // otherwise fall back to localControlPoints (for manually created arbitrary lines)
        const keyPointIndices = [];
        // For pathInfo-based centerlines, use all pathPoints
        for (let i = 0; i < this._centerLinePoints.length; i++) {
            keyPointIndices.push(i);
        }
        const sampledPoints = LineWorkUtils.traceLineWorksOnPointCloud(this._centerLinePoints.map((point) => point.localPoint), DEFAULT_TERRIAN_PROFILE_VIEW_STEP_SIZE);
        const pointCloudOutlines = [];
        site.getPointClouds().forEach((data) => {
            if (data.tileSet.show) {
                const outline = LineWorkUtils.createTilesetOutlineOfLineWorks(sampledPoints, data);
                pointCloudOutlines.push(outline);
            }
            lineProfileData.pointCloudOutline.push(...pointCloudOutlines);
        });
        // const points = this._centerLinePoints.map(
        // 	(point) => point.localPointWithAdjustedHeight,
        // );
        // Create base outline from pointsForDisplay
        // const outline = LineWorkUtils.createOutlineForLineWorks(points);
        const isValidPointForOutline = (point) => {
            return (point.pointType.includes(AlignmentPointType.BEGIN_POINT) ||
                point.pointType.includes(AlignmentPointType.END_POINT) ||
                point.pointType.includes(AlignmentPointType.IP_PROJECTION) ||
                point.pointType.includes(AlignmentPointType.STATION_POINT) ||
                point.pointType.includes(AlignmentPointType.BEGIN_CURVE_POINT) ||
                point.pointType.includes(AlignmentPointType.END_CURVE_POINT));
        };
        const outline = [];
        let dist = 0;
        for (let i = 0; i < this._centerLinePoints.length; ++i) {
            if (this._centerLinePoints[i - 1]) {
                dist += Cartesian2.distance(this._centerLinePoints[i - 1].localPointWithAdjustedHeight, this._centerLinePoints[i].localPointWithAdjustedHeight);
            }
            if (this._centerLinePoints[i] &&
                isValidPointForOutline(this._centerLinePoints[i])) {
                const height = this._centerLinePoints[i].localPointWithAdjustedHeight.z;
                outline.push([dist, height]);
            }
        }
        // Insert slope break points and recalculate indices
        const slopeBreakIndices = [];
        lineProfileData.lineWorksOutline = outline;
        lineProfileData.keyPointIndices = keyPointIndices;
        lineProfileData.slopeBreakIndices = slopeBreakIndices;
        return lineProfileData;
    }
    toCentralLineData() {
        const pathPoints = this._centerLinePoints.map((point) => {
            return {
                position: point.localPointWithAdjustedHeight,
            };
        });
        const centralLineData = {
            pathInfo: {
                pathPoints: pathPoints,
            },
        };
        return centralLineData;
    }
    // ---------------------------Private Methods ----------------------------
    updateSurveyPoints() {
        this._surveyPointsMap.clear();
        const getName = (id) => {
            let point = this._ipLinePoints.find((point) => point.id === id);
            if (!point?.label) {
                point = this._centerLinePoints.find((point) => point.id === id);
            }
            return point?.label ?? "";
        };
        const pointsToBeTransformed = [];
        for (const segment of this.horizontalAlignment.segments) {
            for (const point of segment.surveyPoints) {
                const surveyPoint = this._surveyPointsMap.get(point.id);
                if (!surveyPoint) {
                    const newSurveyPoint = {
                        id: point.id,
                        localPoint: point.localPoint,
                        name: getName(point.id),
                    };
                    pointsToBeTransformed.push(newSurveyPoint);
                    this._surveyPointsMap.set(newSurveyPoint.id, newSurveyPoint);
                }
            }
        }
    }
    /**
     * This updates alignment if alignment is already created.
     * PVIs linked to IP points follow their IP point's new horizontal distance; other PVIs scale by length ratio.
     */
    recomputeAlignmentWhileEdit(editFunc) {
        // Classify against pre-edit state
        const oldLength = this.horizontalAlignment.length;
        const links = linkPvisToIps(this.verticalAlignment.verticalAlignmentData, this.computeIpHorizontalDistances());
        editFunc();
        this.horizontalAlignment.recompute(this._ipLinePoints);
        const verticalAlignmentData = relocatePvis(links, this.computeIpHorizontalDistances(), oldLength, this.horizontalAlignment.length);
        this._verticalAlignment.updateData(verticalAlignmentData);
        this.postCompute();
    }
    computeLength() {
        this._length = 0;
        const points = this._centerLinePoints.map((point) => point.localPointWithAdjustedHeight);
        for (let i = 0; i < points.length - 1; i++) {
            this._length += Cartesian3.distance(points[i], points[i + 1]);
        }
    }
    computeGeometricPoints() {
        // Geometric Points, Station Points , IP Projection Points, PVI Points,
        // compute points with height, it will include all points from horizontal alignment and unique points from vertical alignment and interpolated elevation
        // curve points -> points separated as per curve
        // centerLinePoints -> array of unique points from curve points
        const geometricPoints = [];
        this._curvePoints.length = 0;
        const verticalIntersections = this.getInfoOfPointOfVerticalIntersections();
        const horizontalSegments = this._horizontalAlignment.segments;
        // Geometric points and PVI points
        let cumulativeDistance = 0;
        let verticalIntersectionIndex = 0;
        for (const segment of horizontalSegments) {
            const points = [];
            let previousPoint;
            // need to check verticalIntersection and add point from vertical intersection to curve points and points
            // vertical Intersection is sorted in ascending order
            for (let i = 0; i < segment.curvePoints.length; i++) {
                const point = segment.curvePoints[i];
                if (!previousPoint) {
                    // first point
                    const elevation = this._verticalAlignment.getElevationForHorizontalDistance(cumulativeDistance);
                    const alignmentPoint = {
                        id: createGuid(),
                        localPoint: new Cartesian3(point.x, point.y, elevation),
                        localPointWithAdjustedHeight: new Cartesian3(point.x, point.y, elevation),
                        pointType: [AlignmentPointType.GEOMETRIC_POINT],
                        distanceFromStart: cumulativeDistance,
                        distanceFromPreviousPoint: 0,
                        label: "",
                    };
                    points.push(alignmentPoint);
                    previousPoint = alignmentPoint.localPoint;
                    continue;
                }
                //within this previous point and current point there can be multiple verticalIntersections
                const dist = Cartesian2.distance(point, previousPoint);
                cumulativeDistance += dist;
                while (verticalIntersectionIndex < verticalIntersections.length) {
                    if (cumulativeDistance >
                        verticalIntersections[verticalIntersectionIndex].horizontalDistance) {
                        // add vertical intersection point
                        const distanceFromPreviousPoint = Cartesian2.distance(points[points.length - 1].localPoint, verticalIntersections[verticalIntersectionIndex].point);
                        if (distanceFromPreviousPoint > 0) {
                            points.push({
                                id: createGuid(),
                                localPoint: verticalIntersections[verticalIntersectionIndex].point,
                                localPointWithAdjustedHeight: verticalIntersections[verticalIntersectionIndex].point,
                                pointType: [
                                    AlignmentPointType.GEOMETRIC_POINT,
                                    AlignmentPointType.POINT_OF_VERTICAL_INTERSECTION,
                                ],
                                distanceFromStart: verticalIntersections[verticalIntersectionIndex]
                                    .horizontalDistance,
                                distanceFromPreviousPoint: distanceFromPreviousPoint,
                                label: "",
                            });
                        }
                        verticalIntersectionIndex++;
                    }
                    else if (cumulativeDistance ===
                        verticalIntersections[verticalIntersectionIndex].horizontalDistance) {
                        // if equal there should be already a point
                        const distanceFromPreviousPoint = Cartesian2.distance(points[points.length - 1].localPoint, verticalIntersections[verticalIntersectionIndex].point);
                        points.push({
                            id: createGuid(),
                            localPoint: verticalIntersections[verticalIntersectionIndex].point,
                            localPointWithAdjustedHeight: verticalIntersections[verticalIntersectionIndex].point,
                            pointType: [
                                AlignmentPointType.GEOMETRIC_POINT,
                                AlignmentPointType.POINT_OF_VERTICAL_INTERSECTION,
                            ],
                            distanceFromStart: cumulativeDistance,
                            distanceFromPreviousPoint: distanceFromPreviousPoint,
                            label: "",
                        });
                        previousPoint = points[points.length - 1].localPoint;
                        verticalIntersectionIndex++;
                        break;
                    }
                    else {
                        previousPoint = points[points.length - 1].localPoint;
                        break;
                    }
                }
                const elevation = this._verticalAlignment.getElevationForHorizontalDistance(cumulativeDistance);
                const newPoint = new Cartesian3(point.x, point.y, elevation);
                const distanceFromPreviousPoint = Cartesian2.distance(previousPoint, newPoint);
                if (distanceFromPreviousPoint > 0) {
                    points.push({
                        id: createGuid(),
                        localPoint: newPoint,
                        localPointWithAdjustedHeight: newPoint,
                        pointType: [AlignmentPointType.GEOMETRIC_POINT],
                        distanceFromStart: cumulativeDistance,
                        distanceFromPreviousPoint: distanceFromPreviousPoint,
                        label: "",
                    });
                }
                previousPoint = point;
            }
            this._curvePoints.push({
                type: segment.type,
                points: points.map((p) => p.localPoint),
            });
            if (segment.type === CurveDataType.CIRCULAR_SEGMENT) {
                points[0].pointType.push(AlignmentPointType.BEGIN_CURVE_POINT);
                points[points.length - 1].pointType.push(AlignmentPointType.END_CURVE_POINT);
            }
            if (geometricPoints.length > 0 &&
                points.length > 0 &&
                Cartesian2.equals(geometricPoints[geometricPoints.length - 1].localPoint, points[0].localPoint)) {
                const lastPoint = geometricPoints.pop();
                points[0].pointType.push(...lastPoint.pointType);
            }
            // Making id same as survey points
            points[0].id = segment.startPoint.id;
            points[points.length - 1].id = segment.endPoint.id;
            if (geometricPoints.length > 0) {
                const lastPoint = geometricPoints[geometricPoints.length - 1];
                if (lastPoint.id === points[0].id) {
                    lastPoint.pointType.push(...points[0].pointType);
                    points.shift();
                }
            }
            geometricPoints.push(...points);
        }
        geometricPoints[0].pointType.push(AlignmentPointType.INTERSECTION_POINT, AlignmentPointType.IP_PROJECTION, AlignmentPointType.POINT_OF_VERTICAL_INTERSECTION, AlignmentPointType.BEGIN_POINT);
        geometricPoints[geometricPoints.length - 1].pointType.push(AlignmentPointType.INTERSECTION_POINT, AlignmentPointType.IP_PROJECTION, AlignmentPointType.POINT_OF_VERTICAL_INTERSECTION, AlignmentPointType.END_POINT);
        return geometricPoints;
    }
    computeStationPoints(geometricPoints) {
        const stationPoints = [];
        //interpolate points to get station points -> assign label BP, EP, Nos
        // insert key points -> assign label -> BC, EC
        // insert points close to IP points -> Nos + distance
        const interpolatedPoints = [];
        let stationDistanceTracker = 0;
        for (let i = 1; i < geometricPoints.length; i++) {
            const dist2D = Cartesian2.distance(geometricPoints[i].localPoint, geometricPoints[i - 1].localPoint);
            stationDistanceTracker += dist2D;
            if (stationDistanceTracker >= this._stationInterval) {
                while (stationDistanceTracker >= this._stationInterval) {
                    stationDistanceTracker -= this._stationInterval;
                    const ratio = stationDistanceTracker / dist2D;
                    const interpolatedPoint = Cartesian3.lerp(geometricPoints[i].localPoint, geometricPoints[i - 1].localPoint, ratio, new Cartesian3());
                    interpolatedPoints.push(interpolatedPoint);
                }
            }
        }
        for (let i = 0; i < interpolatedPoints.length; i++) {
            stationPoints.push({
                id: `station_${this.id}_${i + 1}`,
                localPoint: interpolatedPoints[i],
                localPointWithAdjustedHeight: interpolatedPoints[i],
                pointType: [AlignmentPointType.STATION_POINT],
                distanceFromStart: this._stationInterval * (i + 1),
                distanceFromPreviousPoint: this._stationInterval,
                label: "",
            });
        }
        return stationPoints;
    }
    computeIPProjectionPoints() {
        // insert ip points
        const ipProjectionPoints = [];
        for (let i = 1; i < this._ipLinePoints.length - 1; i++) {
            const ipPoint = this._ipLinePoints[i];
            const closestPointInfo = this.getClosestPointInfoOnPath(ipPoint.localPoint);
            const ipProjectionPoint = {
                id: `IP_${ipPoint.id}`,
                localPoint: closestPointInfo.point,
                localPointWithAdjustedHeight: closestPointInfo.point,
                pointType: [AlignmentPointType.IP_PROJECTION],
                distanceFromStart: closestPointInfo.distanceFromStart_2D,
                distanceFromPreviousPoint: Cartesian2.distance(this._ipLinePoints[i - 1].localPoint, closestPointInfo.point),
                label: "",
            };
            ipProjectionPoints.push(ipProjectionPoint);
        }
        return ipProjectionPoints;
    }
    computeCenterLinePoints() {
        this._centerLinePoints = [];
        const geometricPoints = this.computeGeometricPoints();
        this._centerLinePoints.push(...geometricPoints);
        const stationPoints = this.computeStationPoints(geometricPoints);
        const ipProjectionPoints = this.computeIPProjectionPoints();
        this._centerLinePoints.push(...stationPoints);
        this._centerLinePoints.push(...ipProjectionPoints);
        this._centerLinePoints.sort((a, b) => a.distanceFromStart - b.distanceFromStart);
        //recompute distance
        const indicesWithZeroDistance = [];
        for (let i = 1; i < this._centerLinePoints.length; i++) {
            const distanceFromPreviousPoint = Cartesian2.distance(this._centerLinePoints[i].localPoint, this._centerLinePoints[i - 1].localPoint);
            if (distanceFromPreviousPoint === 0) {
                // console.log("Zero distance found between points", i, i-1);
                // console.log("Points", this._centerLinePoints[i], this._centerLinePoints[i - 1]);
                indicesWithZeroDistance.push(i);
            }
            this._centerLinePoints[i].distanceFromPreviousPoint =
                distanceFromPreviousPoint;
            this._centerLinePoints[i].distanceFromStart =
                this._centerLinePoints[i - 1].distanceFromStart +
                    this._centerLinePoints[i].distanceFromPreviousPoint;
        }
        // remove points with zero distance
        this._centerLinePointMergeAliases.clear();
        for (let i = indicesWithZeroDistance.length - 1; i >= 0; i--) {
            const index = indicesWithZeroDistance[i];
            const point = this._centerLinePoints[index];
            const previousPoint = this._centerLinePoints[index - 1];
            this._centerLinePointMergeAliases.set(point.id, previousPoint.id);
            previousPoint.pointType.push(...point.pointType);
            this._centerLinePoints.splice(index, 1);
        }
        // Index IP projection merges by their final surviving point.
        this._survivorRawIpIds.clear();
        for (const removedId of this._centerLinePointMergeAliases.keys()) {
            if (removedId.startsWith("IP_")) {
                const rawIpId = removedId.slice("IP_".length);
                this._survivorRawIpIds.set(this.resolveMergedPointId(removedId), rawIpId);
            }
        }
    }
    adjustHeightOfIPPoints() {
        const ipPoints = this._ipLinePoints.map((ip) => ip.localPoint);
        const ipPointsWithUpdatedHeight = ipPoints.map((ip) => {
            const closestPointInfo = this.getClosestPointInfoOnPath(ip);
            return new Cartesian3(ip.x, ip.y, closestPointInfo.point.z);
        });
        this._ipLinePoints.forEach((ip, i) => {
            ip.localPointWithAdjustedHeight = ipPointsWithUpdatedHeight[i];
        });
    }
    fixLabelsOfCenterLinePoints() {
        // sequence -> BP/EP -> EC ->BC->Station No
        let statonIndex = 1;
        let BCIndex = 1;
        let ECIndex = 1;
        for (let i = 0; i < this._centerLinePoints.length; i++) {
            const point = this._centerLinePoints[i];
            point.label = "";
            if (point.pointType.includes(AlignmentPointType.BEGIN_POINT)) {
                point.label = "BP";
            }
            if (point.pointType.includes(AlignmentPointType.END_POINT)) {
                point.label = "EP";
            }
            if (point.pointType.includes(AlignmentPointType.END_CURVE_POINT)) {
                point.label =
                    point.label.length > 0
                        ? point.label + `/EC-${ECIndex}`
                        : `EC-${ECIndex}`;
                ECIndex++;
            }
            if (point.pointType.includes(AlignmentPointType.BEGIN_CURVE_POINT)) {
                point.label =
                    point.label.length > 0
                        ? point.label + `/BC-${BCIndex}`
                        : `BC-${BCIndex}`;
                BCIndex++;
            }
            if (point.pointType.includes(AlignmentPointType.STATION_POINT)) {
                point.label =
                    point.label.length > 0
                        ? point.label + `/${formatNoLabel(statonIndex)}`
                        : `${formatNoLabel(statonIndex)}`;
                statonIndex++;
            }
            if (point.pointType.includes(AlignmentPointType.IP_PROJECTION) &&
                !point.pointType.includes(AlignmentPointType.BEGIN_POINT) &&
                !point.pointType.includes(AlignmentPointType.END_POINT)) {
                const index = Math.floor(point.distanceFromStart / this._stationInterval);
                const remainingDistance = point.distanceFromStart % this._stationInterval;
                const additionalDistanceLabel = formatNoWithAdditionalDistanceLabel(index, remainingDistance);
                point.label =
                    point.label.length > 0
                        ? point.label + additionalDistanceLabel
                        : additionalDistanceLabel;
            }
        }
    }
    fixLabelsOfIpPoints() {
        for (let i = 0; i < this._ipLinePoints.length; i++) {
            const point = this._ipLinePoints[i];
            if (i === 0) {
                point.label = "BP";
                point.pointType = [AlignmentPointType.BEGIN_POINT];
            }
            else if (i === this._ipLinePoints.length - 1) {
                point.label = "EP";
                point.pointType = [AlignmentPointType.END_POINT];
            }
            else {
                point.label = `IP-${i}`;
                point.pointType = [AlignmentPointType.INTERSECTION_POINT];
            }
        }
    }
    postCompute() {
        this.computeCenterLinePoints();
        this.fixLabelsOfCenterLinePoints();
        this.fixLabelsOfIpPoints();
        this.updateSurveyPoints();
        this.adjustHeightOfIPPoints();
        this.computeLength();
    }
}
//# sourceMappingURL=alignment.js.map